import React, { createContext, useContext, useReducer, useEffect, useMemo, useCallback } from 'react';
import { applyDataPipeline } from '../utils/applyDataPipeline';
import { AUTOSAVE_DEBOUNCE_MS } from '../config/constants';

const emptyData = {
  rawData: [],
  columns: [],
  mapping: {},
  fileName: '',
  isLoaded: false,
  filterRules: [],
  sortRules: [],
};

const initialState = {
  ...emptyData,
  activeChartId: null,
  dataCache: {},        // { [chartId]: { rawData, columns, mapping, fileName } }
};

function dataReducer(state, action) {
  switch (action.type) {
    case 'RESTORE_SESSION': {
      const p = action.payload;
      return { ...p, filterRules: p.filterRules || [], sortRules: p.sortRules || [] };
    }
    case 'LOAD_DATA': {
      const cached = state.activeChartId
        ? { ...state.dataCache, [state.activeChartId]: { rawData: action.rawData, columns: action.columns, mapping: {}, fileName: action.fileName } }
        : state.dataCache;
      return {
        ...state,
        rawData: action.rawData,
        columns: action.columns,
        mapping: {},
        fileName: action.fileName,
        isLoaded: true,
        dataCache: cached,
      };
    }
    case 'SET_MAPPING': {
      const cached = state.activeChartId && state.isLoaded
        ? { ...state.dataCache, [state.activeChartId]: { ...state.dataCache[state.activeChartId], mapping: action.mapping } }
        : state.dataCache;
      return { ...state, mapping: action.mapping, dataCache: cached };
    }
    case 'SWITCH_CHART': {
      const { chartId, keepData } = action;
      // Save current chart's data to cache (if loaded)
      let cache = { ...state.dataCache };
      if (state.activeChartId && state.isLoaded) {
        cache[state.activeChartId] = {
          rawData: state.rawData,
          columns: state.columns,
          mapping: state.mapping,
          fileName: state.fileName,
        };
      }

      // Load target chart's cached data, or carry forward current data, or reset
      const targetCached = cache[chartId];
      if (targetCached) {
        return {
          ...state,
          rawData: targetCached.rawData,
          columns: targetCached.columns,
          mapping: targetCached.mapping,
          fileName: targetCached.fileName,
          isLoaded: true,
          activeChartId: chartId,
          dataCache: cache,
        };
      } else if (keepData && state.isLoaded) {
        // Carry current data forward to the new chart (user chose "Keep Data")
        return { ...state, activeChartId: chartId, dataCache: cache };
      } else {
        // No cached data and not keeping → use sample data
        return { ...state, ...emptyData, activeChartId: chartId, dataCache: cache };
      }
    }
    case 'CLEAR_CHART_DATA': {
      const { chartId } = action;
      const cache = { ...state.dataCache };
      delete cache[chartId];
      // If clearing active chart, reset current data too
      if (chartId === state.activeChartId) {
        return { ...state, ...emptyData, dataCache: cache };
      }
      return { ...state, dataCache: cache };
    }
    case 'UPDATE_DATA': {
      const cached = state.activeChartId
        ? { ...state.dataCache, [state.activeChartId]: { rawData: action.rawData, columns: action.columns, mapping: state.mapping, fileName: state.fileName } }
        : state.dataCache;
      return {
        ...state,
        rawData: action.rawData,
        columns: action.columns,
        dataCache: cached,
      };
    }
    case 'SET_FILTER_RULES':
      return { ...state, filterRules: action.filterRules };
    case 'SET_SORT_RULES':
      return { ...state, sortRules: action.sortRules };
    case 'CLEAR_DATA': {
      // Clear current chart data and remove from cache
      const cache = { ...state.dataCache };
      if (state.activeChartId) delete cache[state.activeChartId];
      return { ...state, ...emptyData, dataCache: cache };
    }
    default:
      return state;
  }
}

const DataContext = createContext(null);

export function DataProvider({ children }) {
  const [state, dispatch] = useReducer(dataReducer, initialState);

  // Auto-save data state to its own IndexedDB key (no shared key, no race)
  useEffect(() => {
    const saveSession = async () => {
      try {
        const { db } = await import('../utils/db');
        await db.set('data_session', state);
      } catch (err) {
        console.error('Failed to save data state:', err);
      }
    };
    const timeoutId = setTimeout(saveSession, AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
  }, [state]);

  const loadData = useCallback((rawData, columns, fileName) =>
    dispatch({ type: 'LOAD_DATA', rawData, columns, fileName }), [dispatch]);

  const setMapping = useCallback((mapping) =>
    dispatch({ type: 'SET_MAPPING', mapping }), [dispatch]);

  const updateData = useCallback((rawData, columns) =>
    dispatch({ type: 'UPDATE_DATA', rawData, columns }), [dispatch]);

  const clearData = useCallback(() =>
    dispatch({ type: 'CLEAR_DATA' }), [dispatch]);

  const switchChart = useCallback((chartId, keepData = false) =>
    dispatch({ type: 'SWITCH_CHART', chartId, keepData }), [dispatch]);

  const clearChartData = useCallback((chartId) =>
    dispatch({ type: 'CLEAR_CHART_DATA', chartId }), [dispatch]);

  const hasDataForChart = useCallback((chartId) =>
    !!state.dataCache[chartId], [state.dataCache]);

  const setFilterRules = useCallback((filterRules) =>
    dispatch({ type: 'SET_FILTER_RULES', filterRules }), [dispatch]);

  const setSortRules = useCallback((sortRules) =>
    dispatch({ type: 'SET_SORT_RULES', sortRules }), [dispatch]);

  const derivedData = useMemo(
    () => applyDataPipeline(state.rawData, state.columns, state.filterRules, state.sortRules),
    [state.rawData, state.columns, state.filterRules, state.sortRules],
  );

  return (
    <DataContext.Provider value={{ ...state, derivedData, loadData, updateData, setMapping, clearData, switchChart, clearChartData, hasDataForChart, setFilterRules, setSortRules, dispatch }}>
      {children}
    </DataContext.Provider>
  );
}

export function useData() {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error('useData must be used inside DataProvider');
  return ctx;
}

export default DataContext;
